import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { chatPersistenceService } from "@/services/chatPersistenceService";
import { getUserSession } from "@/services/authService";
import {
  ConversationHookResult,
  ConversationData,
  processConversationMessages,
  createEnrichedConversation,
} from "@/utils/conversationUtils";

interface InternalConversationData {
  conversationId: string | null;
  isLoading: boolean;
  error: Error | null;
}

export function useFetchInternalConversation(agentId: string | undefined) {
  const [data, setData] = useState<InternalConversationData>({
    conversationId: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    if (!agentId) {
      setData({
        conversationId: null,
        isLoading: false,
        error: new Error("Agent ID is required"),
      });
      return;
    }

    async function fetchConversation() {
      try {
        setData((prev) => ({ ...prev, isLoading: true, error: null }));

        // First, get the current user session
        const session = await getUserSession();
        if (!session?.user?.id) {
          throw new Error("User not authenticated");
        }

        const userId = session.user.id;
        //  console.log(
        // `[FETCH-INTERNAL] Loading conversation for user ${userId} + agent ${agentId}`,
        //  );

        // Step 1: Check localStorage for previously viewed conversation
        const storedConversationId =
          chatPersistenceService.getStoredInternalConversationId(
            userId,
            agentId,
          );

        if (storedConversationId) {
          //  console.log(
          //    `[FETCH-INTERNAL] Found stored conversation: ${storedConversationId}`,
          //  );

          // Step 2: Validate that the stored conversation still exists and belongs to the user
          const isValid =
            await chatPersistenceService.validateStoredInternalConversation(
              userId,
              agentId,
              storedConversationId,
            );

          if (isValid) {
            //    console.log(
            //      `[FETCH-INTERNAL] Using valid stored conversation: ${storedConversationId}`,
            //    );
            setData({
              conversationId: storedConversationId,
              isLoading: false,
              error: null,
            });
            return;
          } else {
            //    console.log(
            //      `[FETCH-INTERNAL] Stored conversation was invalid, will find most recent`,
            //    );
          }
        }

        // Step 3: No valid stored conversation, find the most recently active one
        const mostRecentConversationId =
          await chatPersistenceService.getMostRecentInternalConversation(
            userId,
            agentId,
          );

        if (mostRecentConversationId) {
          //  console.log(
          //    `[FETCH-INTERNAL] Using most recent conversation: ${mostRecentConversationId}`,
          //  );

          // Store this as the new "last viewed" conversation
          chatPersistenceService.storeInternalConversationId(
            userId,
            agentId,
            mostRecentConversationId,
          );

          setData({
            conversationId: mostRecentConversationId,
            isLoading: false,
            error: null,
          });
          return;
        }

        // Step 4: No conversations exist, return null (new conversation will be created when user sends first message)
        //console.log(
        //  `[FETCH-INTERNAL] No conversations found for user ${userId} + agent ${agentId}`,
        //);
        setData({
          conversationId: null,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        console.error(
          "[FETCH-INTERNAL] Error fetching internal conversation:",
          error,
        );
        setData({
          conversationId: null,
          isLoading: false,
          error: error instanceof Error ? error : new Error("Unknown error"),
        });
      }
    }

    fetchConversation();
  }, [agentId]);

  return data;
}
